/**
 * Renders the list of threads in the "For You" tab.
 * Fetches thread data via a Relay GraphQL query to `convoai_homeThreads`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import Heading from '@atlaskit/heading';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { AssistanceServiceProduct } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { extractSourcesFromThread } from '@atlassian/rovo-navigation/home-threads-service';
import { ThreadCard } from '@atlassian/rovo-platform-ui-components/thread-card';
import type { CreateSpaceResponse } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { CreateSpaceModal } from '@atlassian/rovo-spaces/ui/CreateSpaceModal';
import type { SpaceSource } from '@atlassian/rovo-spaces/ui/CreateSpaceModal/types';

import type { rovoForYouTabThreadsListQuery } from './__generated__/rovoForYouTabThreadsListQuery.graphql';
import messages from './messages';
import { Sources } from './sources/Sources';

/** Touchpoint identifier reported for spaces created from the For You tab. */
const FOR_YOU_TOUCHPOINT_SOURCE = 'rovo-for-you-tab';
/** Experience id for the ai-mate surface, forwarded to the create request. */
const AI_MATE_EXPERIENCE_ID = 'ai-mate';

/** Delay in milliseconds before a hover event is fired, to filter out accidental hovers. */
const HOVER_DEBOUNCE_DELAY_MS = 200;

interface ThreadsListProps {
	/** The tenant cloud ID for GraphQL queries. */
	cloudId: string;
	/** The Atlassian Account ID of the current user. */
	userId: string;
	/** Flat list of conversationIds attached to any source on any space (provided by `useListSpaces`).
	 * Used to hide threads from the suggestion list whose conversation has already been organized
	 * into a space. */
	spacesConversationIds: string[];
	/** Called after a new space is successfully created. The parent optimistically inserts the
	 * new space into the SpaceStore and launches into it; `sources` carries the originating
	 * thread's source(s) to seed onto the launched space. */
	onSpaceCreated: (
		space: CreateSpaceResponse,
		emojiId?: string,
		attachedConversationId?: string,
		sources?: SpaceSource[],
	) => void;
	/** Called whenever the empty/non-empty state of the threads list changes. */
	onEmpty?: (isEmpty: boolean) => void;
	/** Host product, used to build the `assistanceServiceParams` forwarded to `<CreateSpaceModal>`
	 * so the create request carries the correct `x-product` header. */
	product: AssistanceServiceProduct;
}

/** Renders a list of ThreadCard components populated by the `convoai_homeThreads` GraphQL query.
 *
 * Threads are filtered to hide any thread whose `id` is already present as a `conversationId`
 * on any source of any space — the user has already organized that thread, so it shouldn't
 * appear as a suggestion to create a new space from. */
export const ThreadsList = ({
	cloudId,
	userId,
	spacesConversationIds,
	onSpaceCreated,
	onEmpty,
	product,
}: ThreadsListProps): React.JSX.Element | null => {
	const intl = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const assistanceServiceParams = useMemo(
		() => ({
			touchpointSource: FOR_YOU_TOUCHPOINT_SOURCE,
			product,
			experienceId: AI_MATE_EXPERIENCE_ID,
			siteId: cloudId,
		}),
		[product, cloudId],
	);

	// Bumping this forces Relay to refetch the home-threads query (e.g. after a new space is
	// created so a freshly-attached thread disappears from the suggestion list).
	const [threadsFetchKey, setThreadsFetchKey] = useState(0);

	const data = useLazyLoadQuery<rovoForYouTabThreadsListQuery>(
		/* eslint-disable @atlassian/relay/unused-fields -- __typename/ari/data consumed by extractSourcesFromThread */
		graphql`
			query rovoForYouTabThreadsListQuery(
				$cloudId: ID!
				$experience: String!
				$homeThreadsInput: ConvoAiHomeThreadsInput!
			) {
				convoai_homeThreads(
					cloudId: $cloudId
					experience: $experience
					homeThreadsInput: $homeThreadsInput
				) {
					edges {
						node {
							id
							title
							sources {
								...Sources_AtlassianRovoForYou
								__typename
								... on ConvoAiHomeThreadsFirstPartySource {
									ari
									url
									data {
										__typename
									}
								}
								... on ConvoAiHomeThreadsThirdPartySource {
									url
								}
							}
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			cloudId,
			experience: 'home-threads',
			homeThreadsInput: { userId },
		},
		{
			fetchPolicy: 'store-or-network',
			fetchKey: threadsFetchKey,
		},
	);

	// Build a Set of conversationIds that are already attached to any source on any space, so we
	// can do O(1) membership checks while filtering. Any thread whose `id` is in this set has
	// effectively been "claimed" by a space and should be hidden from the suggestion list to
	// avoid duplicate work for the user. The flat list is provided by `useListSpaces` so we don't
	// have to re-walk the spaces tree here.
	const threadIdsInSpaces = useMemo(() => new Set(spacesConversationIds), [spacesConversationIds]);

	const allEdges = data.convoai_homeThreads?.edges;
	const edges = useMemo(
		() =>
			allEdges?.filter((edge) => {
				const id = edge.node?.id;
				return id ? !threadIdsInSpaces.has(id) : true;
			}),
		[allEdges, threadIdsInSpaces],
	);

	const isEmpty = !edges || edges.length === 0;

	useEffect(() => {
		onEmpty?.(isEmpty);
	}, [isEmpty, onEmpty]);

	const debouncedSendHoverEvent = useMemo(
		() =>
			debounce((threadId: string) => {
				sendAnalyticsEvent({
					actionSubject: 'threadCard',
					action: 'hovered',
					eventType: 'ui',
					attributes: {
						threadId,
					},
				});
			}, HOVER_DEBOUNCE_DELAY_MS),
		[sendAnalyticsEvent],
	);

	useEffect(() => {
		return () => {
			debouncedSendHoverEvent.cancel();
		};
	}, [debouncedSendHoverEvent]);

	const handleThreadHover = useCallback(
		(threadId: string) => {
			debouncedSendHoverEvent(threadId);
		},
		[debouncedSendHoverEvent],
	);

	const handleThreadHoverLeave = useCallback(() => {
		debouncedSendHoverEvent.cancel();
	}, [debouncedSendHoverEvent]);

	const [isCreateSpaceModalOpen, setIsCreateSpaceModalOpen] = useState(false);
	const [createSpaceInitialName, setCreateSpaceInitialName] = useState<string | undefined>(
		undefined,
	);
	const [createSpaceInitialThreadId, setCreateSpaceInitialThreadId] = useState<string | undefined>(
		undefined,
	);
	const [createSpaceInitialSources, setCreateSpaceInitialSources] = useState<
		SpaceSource[] | undefined
	>(undefined);
	const handleCreateSpaceClick = useCallback(
		(threadId: string, threadTitle: string, sources?: SpaceSource[]) => {
			sendAnalyticsEvent({
				actionSubject: 'createSpaceButton',
				action: 'clicked',
				eventType: 'ui',
				attributes: { threadId },
			});
			setCreateSpaceInitialName(threadTitle);
			setCreateSpaceInitialThreadId(threadId);
			setCreateSpaceInitialSources(sources);
			setIsCreateSpaceModalOpen(true);
		},
		[sendAnalyticsEvent],
	);

	if (isEmpty) {
		return (
			<>
				<ExperienceSuccess name={ExperienceName.THREADS_LIST_QUERY} />
			</>
		);
	}

	return (
		<Stack space="space.200">
			<ExperienceStart name={ExperienceName.THREADS_LIST_QUERY} />
			<Heading size="xxsmall" as="h4">
				{intl.formatMessage(messages.suggestedSpacesSectionHeading)}
			</Heading>
			<Box
				role="group"
				aria-label={intl.formatMessage(messages.threadsListLabel)}
				testId="threads-list"
			>
				<Stack space="space.200">
					{edges.map((edge) => {
						const thread = edge.node;

						if (!thread?.id || !thread.title) {
							return null;
						}

						const threadId = thread.id;

						const nonNullSources = thread.sources?.filter(
							(source): source is NonNullable<typeof source> => source != null,
						);

						// Build the create-space sources using the shared helper (also used by the
						// Recommended Spaces panel and the side-nav recommended card) so first-party
						// sources keep their real ARI, third-party sources fall back to the URL, and
						// `sourceType`/`name`/`conversationIds` are resolved identically.
						const spaceSources =
							nonNullSources && nonNullSources.length > 0
								? extractSourcesFromThread(nonNullSources).spaceSources
								: undefined;
						const modalSources: SpaceSource[] | undefined =
							spaceSources && spaceSources.length > 0 ? spaceSources : undefined;

						return (
							<Box
								key={threadId}
								onMouseEnter={() => handleThreadHover(threadId)}
								onMouseLeave={handleThreadHoverLeave}
								onFocus={() => handleThreadHover(threadId)}
								onBlur={handleThreadHoverLeave}
							>
								<ThreadCard
									threadId={threadId}
									title={thread.title}
									renderIcon={null}
									renderSources={
										nonNullSources && nonNullSources.length > 0 ? (
											<Sources
												sources={nonNullSources}
												testId="rovo-for-you-brief-sources-button"
											/>
										) : undefined
									}
									onCreateProjectClick={(id, title) =>
										handleCreateSpaceClick(id, title, modalSources)
									}
								/>
							</Box>
						);
					})}
				</Stack>
			</Box>
			<ExperienceSuccess name={ExperienceName.THREADS_LIST_QUERY} />
			<CreateSpaceModal
				isOpen={isCreateSpaceModalOpen}
				assistanceServiceParams={assistanceServiceParams}
				onClose={() => {
					setIsCreateSpaceModalOpen(false);
					setCreateSpaceInitialName(undefined);
					setCreateSpaceInitialThreadId(undefined);
					setCreateSpaceInitialSources(undefined);
				}}
				onCreated={(space, emojiId, attachedConversationId) => {
					onSpaceCreated(space, emojiId, attachedConversationId, createSpaceInitialSources);
					// Re-run the home-threads query so the just-organized thread drops out of the
					// suggestion list.
					setThreadsFetchKey((prev) => prev + 1);
				}}
				cloudId={cloudId}
				initialName={createSpaceInitialName}
				initialConversationId={createSpaceInitialThreadId}
				initialSources={createSpaceInitialSources}
			/>
		</Stack>
	);
};
